import { useState, useRef, useCallback } from 'react'
import {
  X, Upload, FolderOpen, Loader2, AlertCircle, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronUp,
} from 'lucide-react'
import { toast } from 'sonner'
import { adminApi } from '@/api/admin'
import { quetureContentApi } from '@/api/quetureContent'
import type {
  ImagePreviewResult, ImagePreviewMatchGroup, ImageBulkSaveEntry,
} from '@/api/quetureContent'

const MAX_FILES = 200
const MAX_FILE_SIZE = 5 * 1024 * 1024
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp']
const CONCURRENT_UPLOADS = 5

type Phase = 'select' | 'preview' | 'uploading' | 'done'

interface UploadResult {
  success: number
  failed: number
  errors: string[]
}

interface Props {
  onClose: () => void
}

export function BuildupBulkImageUpload({ onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('select')
  const [files, setFiles] = useState<File[]>([])
  const [preview, setPreview] = useState<ImagePreviewResult | null>(null)
  const [selectedQuetures, setSelectedQuetures] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' })
  const [result, setResult] = useState<UploadResult | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['matched']))
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      next.has(section) ? next.delete(section) : next.add(section)
      return next
    })
  }

  // ── File Selection ─────────────────────────────────────────────
  const handleFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return

    const imageFiles: File[] = []
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]
      if (!ALLOWED_TYPES.includes(file.type)) continue
      if (file.size > MAX_FILE_SIZE) continue
      imageFiles.push(file)
    }

    if (imageFiles.length === 0) {
      toast.error('유효한 이미지 파일이 없습니다.')
      return
    }
    if (imageFiles.length > MAX_FILES) {
      toast.error(`최대 ${MAX_FILES}장까지 업로드할 수 있습니다. (${imageFiles.length}장 선택됨)`)
      return
    }

    setFiles(imageFiles)
    setLoading(true)

    try {
      const fileNames = imageFiles.map((f) => f.name)
      const previewResult = await quetureContentApi.previewBuildupImages(fileNames)
      setPreview(previewResult)

      // Auto-select queture if only one candidate with buildup
      const autoSelected = new Map<string, string>()
      for (const group of previewResult.matched) {
        const withBuildup = group.candidates.filter((c) => c.hasBuildupData)
        if (withBuildup.length === 1) {
          autoSelected.set(group.code, withBuildup[0].quetureId)
        }
      }
      setSelectedQuetures(autoSelected)
      setPhase('preview')
    } catch {
      toast.error('프리뷰 생성에 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  // ── Upload ─────────────────────────────────────────────────────
  const getUploadableFiles = useCallback(() => {
    if (!preview) return []
    const uploadable: Array<{ file: File; code: string; step: number; itemNumber: number; location: string; order: number }> = []

    for (const group of preview.matched) {
      const quetureId = selectedQuetures.get(group.code)
      if (!quetureId) continue
      const candidate = group.candidates.find((c) => c.quetureId === quetureId)
      if (!candidate?.hasBuildupData) continue

      for (const fileInfo of group.files) {
        const file = files.find((f) => f.name === fileInfo.fileName)
        if (!file) continue
        uploadable.push({
          file,
          code: group.code,
          step: fileInfo.step,
          itemNumber: fileInfo.itemNumber,
          location: fileInfo.location,
          order: fileInfo.order,
        })
      }
    }
    return uploadable
  }, [preview, selectedQuetures, files])

  const handleUpload = useCallback(async () => {
    const uploadable = getUploadableFiles()
    if (uploadable.length === 0) {
      toast.error('업로드할 파일이 없습니다.')
      return
    }

    setPhase('uploading')
    setUploadProgress({ current: 0, total: uploadable.length, currentFile: '' })

    const uploadedUrls = new Map<string, { step: number; itemNumber: number; location: string; order: number; url: string }>()
    const errors: string[] = []
    let completed = 0

    const uploadFile = async (item: typeof uploadable[0]) => {
      let retries = 3
      while (retries > 0) {
        try {
          const uploadResult = await adminApi.uploadImage(item.file, 'buildup')
          const key = `${item.code}_STEP${item.step}_${item.itemNumber}_${item.location}_${item.order}`
          uploadedUrls.set(key, {
            step: item.step,
            itemNumber: item.itemNumber,
            location: item.location,
            order: item.order,
            url: uploadResult.url,
          })
          completed++
          setUploadProgress({ current: completed, total: uploadable.length, currentFile: item.file.name })
          return
        } catch {
          retries--
          if (retries === 0) {
            errors.push(item.file.name)
            completed++
            setUploadProgress({ current: completed, total: uploadable.length, currentFile: item.file.name })
          }
        }
      }
    }

    // Process in batches of CONCURRENT_UPLOADS
    for (let i = 0; i < uploadable.length; i += CONCURRENT_UPLOADS) {
      const batch = uploadable.slice(i, i + CONCURRENT_UPLOADS)
      await Promise.all(batch.map(uploadFile))
    }

    // Group uploaded URLs by quetureId + step + location
    const saveEntries: ImageBulkSaveEntry[] = []
    for (const group of preview!.matched) {
      const quetureId = selectedQuetures.get(group.code)
      if (!quetureId) continue

      const byStepItemLocation = new Map<string, { step: number; itemNumber: number; location: string; urls: Array<{ order: number; url: string }> }>()
      for (const fileInfo of group.files) {
        const key = `${group.code}_STEP${fileInfo.step}_${fileInfo.itemNumber}_${fileInfo.location}_${fileInfo.order}`
        const uploaded = uploadedUrls.get(key)
        if (!uploaded) continue

        const groupKey = `${fileInfo.step}_${fileInfo.itemNumber}_${fileInfo.location}`
        if (!byStepItemLocation.has(groupKey)) {
          byStepItemLocation.set(groupKey, { step: fileInfo.step, itemNumber: fileInfo.itemNumber, location: fileInfo.location, urls: [] })
        }
        byStepItemLocation.get(groupKey)!.urls.push({ order: uploaded.order, url: uploaded.url })
      }

      for (const [, entry] of byStepItemLocation) {
        entry.urls.sort((a, b) => a.order - b.order)
        saveEntries.push({
          quetureId,
          step: entry.step,
          itemNumber: entry.itemNumber,
          location: entry.location as ImageBulkSaveEntry['location'],
          imageUrls: entry.urls.map((u) => u.url),
        })
      }
    }

    if (saveEntries.length > 0) {
      try {
        await quetureContentApi.bulkSaveBuildupImages(saveEntries)
      } catch {
        toast.error('이미지 URL 저장에 실패했습니다.')
      }
    }

    setResult({ success: completed - errors.length, failed: errors.length, errors })
    setPhase('done')
  }, [getUploadableFiles, preview, selectedQuetures])

  const uploadableCount = getUploadableFiles().length

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">빌드업 이미지 벌크 업로드</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Phase: Select */}
          {phase === 'select' && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }}
              >
                {loading ? (
                  <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto" />
                ) : (
                  <>
                    <FolderOpen className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">폴더를 선택하거나 이미지를 드래그&드롭 하세요</p>
                    <p className="text-xs text-gray-400">지원 형식: PNG, JPEG, GIF, WebP, SVG | 파일당 최대 5MB | 최대 200장</p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                    >
                      폴더 선택
                    </button>
                  </>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-600 mb-1">파일명 규칙:</p>
                <code className="text-xs text-gray-500 break-all">
                  XX-XX-XX_빌드업_STEP&#123;단계&#125;_&#123;문항&#125;_&#123;문제|발문|해설&#125;[_&#123;이미지순번&#125;][_p].png
                </code>
                <div className="mt-1 space-y-0.5 text-xs text-gray-400">
                  <p>예: 01-01-01_빌드업_STEP2_1_해설.png</p>
                  <p>인쇄용: 01-01-01_빌드업_STEP2_1_해설_p.png</p>
                  <p>이미지가 여러 장이면 해설_1.png, 해설_2.png처럼 순번을 붙입니다.</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                // @ts-expect-error webkitdirectory is not in the type
                webkitdirectory=""
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
          )}

          {/* Phase: Preview */}
          {phase === 'preview' && preview && (
            <div className="space-y-4">
              <div className="flex gap-3 text-sm">
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="w-4 h-4" /> 매칭: {preview.matched.reduce((sum, g) => sum + g.files.length, 0)}장
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <AlertCircle className="w-4 h-4" /> 에러: {preview.errors.length}장
                </span>
              </div>

              {preview.matched.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleSection('matched')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
                  >
                    {expandedSections.has('matched') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    매칭 결과 ({preview.matched.length}개 코드)
                  </button>
                  {expandedSections.has('matched') && (
                    <div className="space-y-3">
                      {preview.matched.map((group) => (
                        <MatchGroupCard
                          key={group.code}
                          group={group}
                          selectedQuetureId={selectedQuetures.get(group.code)}
                          onSelect={(qid) => {
                            setSelectedQuetures((prev) => {
                              const next = new Map(prev)
                              next.set(group.code, qid)
                              return next
                            })
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {preview.errors.length > 0 && (
                <div>
                  <button
                    onClick={() => toggleSection('errors')}
                    className="flex items-center gap-2 text-sm font-medium text-red-600 mb-2"
                  >
                    {expandedSections.has('errors') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    에러 ({preview.errors.length}장)
                  </button>
                  {expandedSections.has('errors') && (
                    <div className="space-y-1">
                      {preview.errors.map((err, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs bg-red-50 px-3 py-2 rounded-lg">
                          <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                          <div>
                            <span className="font-mono text-red-700">{err.fileName}</span>
                            <span className="text-red-500 ml-2">
                              {err.reason === 'INVALID_FORMAT' && '— 파일명 규칙 불일치'}
                              {err.reason === 'NO_MATCH' && '— 매칭되는 깨처 없음'}
                              {err.reason === 'NO_BUILDUP' && '— 빌드업 데이터 없음'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Phase: Uploading */}
          {phase === 'uploading' && (
            <div className="py-12 text-center space-y-4">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto" />
              <div className="w-full bg-gray-200 rounded-full h-3 max-w-md mx-auto">
                <div
                  className="bg-indigo-600 h-3 rounded-full transition-all"
                  style={{ width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total * 100) : 0}%` }}
                />
              </div>
              <p className="text-sm text-gray-600">
                {uploadProgress.current}/{uploadProgress.total} ({Math.round(uploadProgress.current / Math.max(uploadProgress.total, 1) * 100)}%)
              </p>
              <p className="text-xs text-gray-400 font-mono truncate max-w-md mx-auto">{uploadProgress.currentFile}</p>
            </div>
          )}

          {/* Phase: Done */}
          {phase === 'done' && result && (
            <div className="py-12 text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
              <h3 className="text-lg font-semibold text-gray-900">업로드 완료</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p>성공: <span className="font-semibold text-green-600">{result.success}장</span></p>
                {result.failed > 0 && (
                  <p>실패: <span className="font-semibold text-red-600">{result.failed}장</span></p>
                )}
              </div>
              {result.errors.length > 0 && (
                <div className="text-left max-w-md mx-auto bg-red-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-600 mb-1">실패한 파일:</p>
                  {result.errors.map((name, i) => (
                    <p key={i} className="text-xs text-red-500 font-mono">{name}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          {phase === 'select' && (
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">
              닫기
            </button>
          )}
          {phase === 'preview' && (
            <>
              <button
                onClick={() => { setPhase('select'); setPreview(null); setFiles([]) }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                다시 선택
              </button>
              <button
                onClick={handleUpload}
                disabled={uploadableCount === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                업로드 ({uploadableCount}장)
              </button>
            </>
          )}
          {phase === 'done' && (
            <button onClick={onClose} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              완료
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────

function MatchGroupCard({
  group, selectedQuetureId, onSelect,
}: {
  group: ImagePreviewMatchGroup
  selectedQuetureId: string | undefined
  onSelect: (qid: string) => void
}) {
  const candidatesWithBuildup = group.candidates.filter((c) => c.hasBuildupData)
  const hasNoBuildupCandidates = candidatesWithBuildup.length === 0

  const filesByStepLocation = new Map<string, string[]>()
  for (const f of group.files) {
    const locationLabel: Record<string, string> = {
      instruction: '발문', explanation: '해설',
      instruction_print: '발문(인쇄)', explanation_print: '해설(인쇄)',
    }
    const key = `STEP${f.step}-${f.itemNumber} ${locationLabel[f.location] ?? f.location}`
    if (!filesByStepLocation.has(key)) filesByStepLocation.set(key, [])
    filesByStepLocation.get(key)!.push(f.fileName)
  }

  return (
    <div className={`border rounded-lg p-3 ${hasNoBuildupCandidates ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-sm font-semibold text-indigo-600">{group.code}</span>
        <span className="text-xs text-gray-400">{group.files.length}장</span>
      </div>

      {hasNoBuildupCandidates ? (
        <div className="flex items-center gap-1.5 text-xs text-yellow-700">
          <AlertTriangle className="w-3.5 h-3.5" />
          빌드업 데이터가 있는 깨처가 없습니다 — 건너뜀
        </div>
      ) : (
        <>
          <select
            value={selectedQuetureId ?? ''}
            onChange={(e) => onSelect(e.target.value)}
            className="w-full text-xs border rounded-lg px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">깨처 선택...</option>
            {candidatesWithBuildup.map((c) => (
              <option key={c.quetureId} value={c.quetureId}>
                {c.quetureName}
              </option>
            ))}
          </select>

          <div className="space-y-0.5">
            {Array.from(filesByStepLocation.entries()).map(([key, fileNames]) => (
              <div key={key} className="text-xs text-gray-500">
                <span className="font-medium text-gray-600">{key}:</span> {fileNames.join(', ')}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
